/**
 * 数字の入力欄(2026-09-10 指示: 入力途中から「,」が入る)。
 * 親が持つのは区切り無しの文字列(そのまま Number() できる)。画面には3桁区切りで出し、
 * 区切りが増減してもカーソルが飛ばないよう、数字の何桁目にいたかで位置を戻す。
 */
import { useLayoutEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { formatNum, stripNum } from '@core/lib/numInput';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** 区切り無しの値(''=未入力) */
  value: string;
  /** 区切りを外した値を返す */
  onChange: (raw: string) => void;
  /** 小数を許す(時間・率) */
  decimal?: boolean;
};

export default function NumInput({ value, onChange, decimal = false, ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  /** 直前の入力でカーソルの左に何桁あったか */
  const digits = useRef<number | null>(null);
  /** 数字以外を弾いたときは親の値が変わらず再描画されないため、毎回描き直す */
  const [, bump] = useState(0);
  const shown = formatNum(value, decimal);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.value !== shown) el.value = shown;
    if (digits.current === null) return;
    let left = digits.current;
    digits.current = null;
    let pos = 0;
    while (pos < el.value.length && left > 0) {
      if (/[0-9]/.test(el.value[pos] ?? '')) left--;
      pos++;
    }
    while (pos < el.value.length && el.value[pos] === ',') pos++;
    el.setSelectionRange(pos, pos);
  });

  /** 表示だけの「,」を消したときに何も起きないと消せなくなるので、隣の数字を消す */
  const apply = (next: string, caret: number) => {
    digits.current = next.slice(0, caret).replace(/[^0-9]/g, '').length;
    bump((n) => n + 1);
    onChange(stripNum(formatNum(next, decimal), decimal));
  };

  return (
    <input
      {...rest}
      ref={ref}
      inputMode={decimal ? 'decimal' : 'numeric'}
      value={shown}
      onKeyDown={(e) => {
        rest.onKeyDown?.(e);
        const el = e.currentTarget;
        const at = el.selectionStart ?? 0;
        if (el.selectionStart !== el.selectionEnd) return;
        if (e.key === 'Backspace' && at >= 2 && el.value[at - 1] === ',') {
          e.preventDefault();
          apply(el.value.slice(0, at - 2) + el.value.slice(at), at - 2);
        } else if (e.key === 'Delete' && el.value[at] === ',') {
          e.preventDefault();
          apply(el.value.slice(0, at) + el.value.slice(at + 2), at);
        }
      }}
      onChange={(e) => {
        const el = e.target;
        apply(el.value, el.selectionStart ?? el.value.length);
      }}
    />
  );
}
